/**
 * A `/game/…` page declares the ONE sport it renders, from its route param,
 * and the ONE event, by the wire keys its rows carry — and, on an
 * edge-served sport, the event's ROWS by ticker (g1xl-v2#1053).
 *
 * Danielle R10 DNL-010: the fallback treats every /game/* path as
 * sport-scoped, so every page mounted under it must declare, or it silently
 * asks for every sport. The legacy
 * sport-less route shape (no `:sport` segment) falls back to all.
 *
 * **The page names its rows** (Dan, 2026-10-13). The edge filter's axes AND
 * (HYDRA_API "Markets filter"), so "the board's tickers OR this game" cannot
 * be said on one connection: a page declaring its sport + event beside a
 * board declaring its tickers + the sport fold unioned to the WHOLE sport on
 * the main edge (gbuff 2026-10-13: he2 13,669 CFB rows at 250 ms for a board
 * of 58 tickers and one open game; hepu-cfbmain-f01 the same). So on an
 * edge-served sport the page runs the board's index cycle
 * (`edgeIndexCycle`): it reads its game's rows from each owning edge's REST
 * index — the K/Intl owner and the separate Poly US owner — with
 * `?game=<its wire keys>` and every class, and declares them on the
 * `tickers` axis beside the sport + event fold. The union beside a board is
 * then the board's tickers ∪ the game's, and everything stays edge-served.
 * The ALT edge keeps its `{<SPORT>ALT, games:[G]}` bound (named with the
 * event; `applyAltOnlyMute`), so the main edge's list never ANDs away the
 * ALT rows. A market newly listed in the open game joins the edge's list at
 * the next re-index (60 s, the board's cadence); until then the core floor
 * carries it, like any row off the list.
 *
 * Until the list lands — the rows have not named the game yet, the owner is
 * unknown, the index is slow or failing — the page is MUTED on the edge
 * (provisional while the first answer is on its way) and the core floor
 * carries the sport. Never sport-wide on an edge-served sport. A core- or
 * firehose-served sport keeps the declaration of before, two-phase: the
 * sport alone until the rows land, then the sport + its ALT key + the event.
 *
 * Cold entry discovers those keys through the owning edge REST catalogue
 * when the stream has not yet supplied rows (#1392). No whole-sport stream
 * subscription is needed for discovery. Keys MUST come from the producer.
 * Anything composed here would be a
 * different shape than the engines match (see `marketStore.getGameWireKeys`),
 * and an allow-list that matches nothing blanks the page.
 */
import { useEffect, useRef } from 'react';
import { declareFeedNeeds } from '../stores/feedNeeds';
import { feedFilterBoardEnabled } from '../clients/feedFilterConfig';
import { fetchMarketsIndex } from '../lib/feedFilter/boardIndex';
import { discoverRouteGameKeys, type RouteGameIdentity } from '../lib/feedFilter/routeGameIndex';
import { altTokenFor, normalizeSport } from '../stores/marketFrameNormalization';
import type { FeedNeeds } from '../workers/feedFilter';
import { feedNeedsKey } from './useFeedNeeds';
import { startIndexCycle, type IndexCycle, type IndexCycleState } from './edgeIndexCycle';

const _status = new Map<string, IndexCycleState>();

/** Diagnostics / tests — what a `/game/*` page's index cycle declared last;
 *  null when the page does not run one (no sport, or the feature off). */
export function getRouteSportFeedNeedsStatus(sourceId: string): IndexCycleState | null {
  return _status.get(sourceId) ?? null;
}

const tickersOf = (rows: ReadonlyArray<{ ticker?: unknown }>): string[] =>
  rows.map((r) => r.ticker).filter((t): t is string => typeof t === 'string' && t.length > 0);

export function useRouteSportFeedNeeds(
  sourceId: string,
  routeSport: string | undefined,
  /** The wire keys this page's own rows carry. Omit (or pass an empty list)
   *  before the rows land. */
  gameWireKeys?: readonly string[] | null,
  route?: RouteGameIdentity,
): void {
  const routeKey = JSON.stringify(route ?? null);
  const routeRef = useRef(route);
  useEffect(() => { routeRef.current = JSON.parse(routeKey) as RouteGameIdentity | undefined; }, [routeKey]);
  const sport = routeSport ? normalizeSport(routeSport) : null;
  // Join to a primitive so a fresh array of the same keys is not a new need.
  const gamesKey = gameWireKeys && gameWireKeys.length > 0
    ? [...new Set(gameWireKeys)].sort().join(' ')
    : '';
  const gamesRef = useRef<string[]>([]);
  // Name the rows on the tickers axis — the board's machinery, the board's switch.
  const named = sport != null && feedFilterBoardEnabled();
  const lastKeyRef = useRef<string | null>(null);
  const cycleRef = useRef<IndexCycle | null>(null);
  // The game the running cycle last indexed for (see the second effect).
  const cycleGamesRef = useRef('');

  const declare = (needs: FeedNeeds): void => {
    const key = feedNeedsKey(needs);
    if (key === lastKeyRef.current) return;
    lastKeyRef.current = key;
    declareFeedNeeds(sourceId, needs);
  };
  // The declaration of before (two-phase): the sport alone until the rows
  // land, then the event. The page renders the sport's ALT lines too
  // (props, team stats/totals, period lines), which a split sport serves
  // under its `<SPORT>ALT` key from a second engine (main-alt-partition-
  // he6-he7 D5.3); the ALT key is named ONLY beside the games axis
  // (g1xl-v2#1053 Part 2c) — alone it asked the ALT edge for every period
  // line and prop of every game of the sport. On a sport that is not split
  // the extra token matches nothing and costs nothing.
  const sportWide = (s: NonNullable<typeof sport>, games: readonly string[]): FeedNeeds =>
    (games.length > 0 ? { sports: [s, altTokenFor(s)], games: [...games] } : { sports: [s] });

  // The current game, for the cycle's callbacks. Declared FIRST, so the
  // effects below (same commit) read the new keys.
  useEffect(() => {
    gamesRef.current = gamesKey ? gamesKey.split(' ') : [];
  }, [gamesKey, routeKey]);

  useEffect(() => {
    if (!named || sport == null) return;
    const wire = sport.toUpperCase();
    // Beside the rows: the sport + event fold (what carries them if a
    // ticker-broad sibling keeps the allow-list off the wire), and the ALT
    // key with the event so the ALT edge is unmuted for this game only. No
    // event yet → the sport alone, no game of its own (never game-broad).
    const fold = (): FeedNeeds => {
      const games = gamesRef.current;
      return games.length > 0 ? { sports: [sport, altTokenFor(sport)], games: [...games] } : { sports: [sport], games: [] };
    };
    const indexGame = async (rest: Parameters<typeof fetchMarketsIndex>[0], signal: AbortSignal) => {
      // Cold detail entry cannot depend on rows withheld by its own filter.
      // Discover wire identities from the REST catalogue, never invent a key
      // or subscribe the whole sport to break that cycle.
      if (gamesRef.current.length === 0 && routeRef.current) {
        const keys = await discoverRouteGameKeys(rest, wire, routeRef.current, signal);
        signal.throwIfAborted();
        if (keys.length === 0) throw new Error('Game not yet present in market index');
        gamesRef.current = keys;
      }
      return fetchMarketsIndex(rest, { sport: wire, classes: [], games: gamesRef.current }, { signal });
    };
    cycleGamesRef.current = gamesRef.current.join(' ');
    const cycle = startIndexCycle({
      sport,
      ready: () => gamesRef.current.length > 0 || Boolean(routeRef.current?.date && routeRef.current.teamA && routeRef.current.teamB),
      fetchPick: indexGame,
      pickTickers: tickersOf,
      fetchUsTickers: async (rest, _pick, signal) => tickersOf(await indexGame(rest, signal)),
      broad: () => declare(sportWide(sport, gamesRef.current)),
      muted: (provisional) => declare(provisional ? { ...fold(), tickers: [], provisional: true } : { ...fold(), tickers: [] }),
      tickers: (names) => declare({ ...fold(), tickers: [...names] }),
      onState: (s) => { _status.set(sourceId, s); },
    });
    cycleRef.current = cycle;
    return () => { cycle.stop(); cycleRef.current = null; _status.delete(sourceId); };
    // `declare` / `sportWide` read refs and `sourceId` only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, sport, named, routeKey]);

  // The game moved (the rows landed and named it; a doubleheader's other
  // game; a key added as more rows arrived): the running cycle re-declares
  // its answer with the new event at once — the ALT edge follows the game
  // immediately — and re-indexes. A held list stays until the new one lands.
  useEffect(() => {
    const cycle = cycleRef.current;
    if (cycle != null) {
      if (gamesKey === cycleGamesRef.current) return;
      cycleGamesRef.current = gamesKey;
      cycle.refresh();
      return;
    }
    declare(sport == null ? { sports: 'all' } : sportWide(sport, gamesKey ? gamesKey.split(' ') : []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceId, sport, named, gamesKey]);

  useEffect(() => () => { declareFeedNeeds(sourceId, null); lastKeyRef.current = null; }, [sourceId]);
}
